import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TopBar from "@/components/TopBar";

function Letters({ word, count, name, testId }) {
  return (
    <div className="rounded-2xl border bg-white p-4" data-testid={testId}>
      <p className="text-sm font-bold tracking-wide">{(name || "").toUpperCase()}</p>
      <div className="mt-2 flex gap-2">
        {word.map((letter, index) => (
          <span
            key={index}
            className="flex h-9 w-8 items-center justify-center rounded-lg border text-lg font-bold"
            style={index < count ? { background: "var(--brand)", color: "#fff" } : { color: "var(--ink-soft)" }}
          >
            {index < count ? letter : "_"}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function LoserMatch() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [trick, setTrick] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get(`/loser/${matchId}`).then(({ data }) => setState(data)).catch((e) => setError(errMsg(e)));
  }, [matchId]);

  useEffect(load, [load]);

  const call = async (path, body) => {
    setError("");
    try {
      const { data } = await api.post(`/loser/${matchId}/${path}`, body || {});
      setState(data);
      setTrick("");
    } catch (e) {
      setError(errMsg(e));
    }
  };

  if (!user) return <div className="p-10 text-center text-sm"><Link to="/login" className="underline">Bitte anmelden</Link></div>;
  if (!state) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">{error || "wird geladen …"}</div>;

  const word = state.word_letters || [];
  const iAmA = state.player_a === user.id;
  const myTurnToSet = state.setter_id === user.id;
  const current = state.current_trick;
  const attempt = current?.attempt;

  return (
    <div data-testid="loser-page">
      <TopBar back="/spielen" title={state.word || "LOSER"} />
      <div className="mx-auto max-w-2xl px-5 py-7">
        <h1 className="text-4xl">{state.word}</h1>
        <p className="mt-1 text-sm text-[var(--ink-soft)]">{state.player_a_name} gegen {state.player_b_name}</p>

        <div className="mt-6 space-y-3">
          <Letters word={word} count={state.a_letters} name={state.player_a_name} testId="loser-letters-a" />
          <Letters word={word} count={state.b_letters} name={state.player_b_name} testId="loser-letters-b" />
        </div>

        {error ? <p className="mt-4 text-sm text-red-700" data-testid="loser-error">{error}</p> : null}

        {state.status === "INVITED" ? (
          <div className="mt-7 rounded-2xl border bg-white p-5" data-testid="loser-invite">
            {iAmA ? (
              <p className="text-sm">Warte, bis {state.player_b_name} die Herausforderung annimmt.</p>
            ) : (
              <>
                <p className="text-base">{state.player_a_name} fordert dich zu {state.word} heraus.</p>
                <div className="mt-4 flex gap-2">
                  <button className="btn btn-primary !py-2.5 !text-xs" onClick={() => call("respond", { accept: true })} data-testid="loser-accept">ANNEHMEN</button>
                  <button className="btn btn-ghost !py-2.5 !text-xs" onClick={() => call("respond", { accept: false })} data-testid="loser-decline">ABLEHNEN</button>
                </div>
              </>
            )}
          </div>
        ) : null}

        {state.status === "DECLINED" ? (
          <p className="mt-7 rounded-2xl border bg-white p-5 text-sm" data-testid="loser-declined">Die Herausforderung wurde abgelehnt.</p>
        ) : null}

        {state.status === "ACTIVE" ? (
          <div className="mt-7 rounded-2xl border bg-white p-5" data-testid="loser-active">
            <p className="eyebrow">Am Zug: {state.setter_id === state.player_a ? state.player_a_name : state.player_b_name}</p>
            {!current ? (
              myTurnToSet ? (
                <div className="mt-3">
                  <div className="eyebrow mb-1">Welchen Trick setzt du?</div>
                  <textarea className="field min-h-[80px]" value={trick} onChange={(e) => setTrick(e.target.value)} data-testid="loser-trick-input" />
                  <button className="btn btn-primary mt-3 w-full" onClick={() => call("set-trick", { description: trick })} data-testid="loser-set-trick">
                    TRICK GESETZT
                  </button>
                </div>
              ) : (
                <p className="mt-3 text-sm text-[var(--ink-soft)]">Dein Gegner setzt gerade einen Trick.</p>
              )
            ) : (
              <div className="mt-3">
                <p className="rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm" data-testid="loser-current-trick">{current.description}</p>
                {!attempt ? (
                  myTurnToSet ? (
                    <p className="mt-3 text-sm text-[var(--ink-soft)]">Dein Gegner versucht den Trick.</p>
                  ) : (
                    <div className="mt-4 flex gap-2">
                      <button className="btn btn-primary !py-2.5 !text-xs" onClick={() => call("attempt", { success: true })} data-testid="loser-attempt-success">GESCHAFFT</button>
                      <button className="btn btn-ghost !py-2.5 !text-xs" onClick={() => call("attempt", { success: false })} data-testid="loser-attempt-fail">NICHT GESCHAFFT</button>
                    </div>
                  )
                ) : myTurnToSet ? (
                  <div className="mt-4">
                    <p className="text-sm">
                      Dein Gegner gibt an: <strong>{attempt.success ? "geschafft" : "nicht geschafft"}</strong>
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button className="btn btn-primary !py-2.5 !text-xs" onClick={() => call("confirm-attempt", { approve: true })} data-testid="loser-confirm">BESTÄTIGEN</button>
                      <button className="btn btn-ghost !py-2.5 !text-xs" onClick={() => call("confirm-attempt", { approve: false })} data-testid="loser-reject">STIMMT NICHT</button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-[var(--ink-soft)]">Dein Versuch wartet auf Bestätigung.</p>
                )}
              </div>
            )}
          </div>
        ) : null}

        {state.status === "FINISHED" ? (
          <div className="mt-7 rounded-2xl border bg-white p-5" data-testid="loser-finished">
            <p className="text-2xl">
              Gewinner: {state.winner_id === state.player_a ? state.player_a_name : state.player_b_name}
            </p>
            <p className="mt-1 text-sm text-[var(--ink-soft)]">
              Verlierer: {state.loser_id === state.player_a ? state.player_a_name : state.player_b_name} · {(state.rounds || []).length} Runden ·{" "}
              {new Date(state.finished_at || state.created_at).toLocaleDateString("de-AT")}
            </p>
            <p className="mt-2 text-sm">Endstand: {state.a_letters} : {state.b_letters} Buchstaben</p>
            <button
              className="btn btn-primary mt-4 !py-2.5 !text-xs"
              onClick={async () => {
                try {
                  const other = iAmA ? state.player_b : state.player_a;
                  const { data } = await api.post("/loser", { game_id: state.game_id, opponent_id: other });
                  navigate(`/loser/${data.id}`);
                } catch (e) {
                  setError(errMsg(e));
                }
              }}
              data-testid="loser-rematch"
            >
              REMATCH
            </button>
          </div>
        ) : null}

        {(state.rounds || []).length ? (
          <section className="mt-8" data-testid="loser-rounds">
            <p className="eyebrow">Runden</p>
            <ul className="mt-3 divide-y overflow-hidden rounded-2xl border bg-white">
              {state.rounds.map((round) => (
                <li key={round.round} className="px-4 py-3 text-sm">
                  <span className="font-semibold">Runde {round.round}</span> · {round.trick} ·{" "}
                  {round.success ? "geschafft" : "nicht geschafft"}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  );
}
